'use client';
import React, { useState } from 'react';
import { ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import type { VenueFilters } from './VenueListingContent';

const eventTypeOptions = ['Wedding', 'Birthday', 'Corporate', 'Reception', 'Conference', 'Anniversary'];
const amenityOptions = ['Catering', 'Parking', 'WiFi', 'AV Setup', 'DJ', 'Bar', 'Valet', 'Garden', 'Stage', 'Decor'];
const ratingOptions = [
  { value: 4.5, label: '4.5+ Excellent' },
  { value: 4.0, label: '4.0+ Very Good' },
  { value: 3.5, label: '3.5+ Good' },
];
const availabilityOptions = [
  { value: 'all', label: 'All Venues' },
  { value: 'available', label: 'Available Only' },
];

type Props = {
  filters: VenueFilters;
  setFilters: React.Dispatch<React.SetStateAction<VenueFilters>>;
  onReset: () => void;
  activeCount: number;
};

function FilterSection({ title, children, defaultOpen = true }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-100 pb-4 mb-4">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-1 text-sm font-bold text-gray-800 hover:text-red-600 transition-colors"
      >
        {title}
        {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
      </button>
      {open && <div className="mt-3">{children}</div>}
    </div>
  );
}

export default function FilterSidebar({ filters, setFilters, onReset, activeCount }: Props) {
  const toggleEventType = (type: string) => {
    setFilters((f) => ({
      ...f,
      eventTypes: f.eventTypes.includes(type)
        ? f.eventTypes.filter((t) => t !== type)
        : [...f.eventTypes, type],
    }));
  };

  const toggleAmenity = (amenity: string) => {
    setFilters((f) => ({
      ...f,
      amenities: f.amenities.includes(amenity)
        ? f.amenities.filter((a) => a !== amenity)
        : [...f.amenities, amenity],
    }));
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-5 sticky top-20">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="font-bold text-gray-900">Filters</h3>
          {activeCount > 0 && (
            <p className="text-xs text-red-500 font-medium mt-0.5">{activeCount} active</p>
          )}
        </div>
        {activeCount > 0 && (
          <button
            onClick={onReset}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-red-600 font-medium transition-colors"
          >
            <RotateCcw size={12} />
            Reset all
          </button>
        )}
      </div>

      {/* Availability */}
      <FilterSection title="Availability">
        <div className="flex flex-col gap-2">
          {availabilityOptions.map((opt) => (
            <label key={`avail-${opt.value}`} className="flex items-center gap-2.5 cursor-pointer group">
              <input
                type="radio"
                name="availability"
                value={opt.value}
                checked={filters.availability === opt.value}
                onChange={() => setFilters((f) => ({ ...f, availability: opt.value }))}
                className="w-4 h-4 accent-red-600"
              />
              <span className="text-sm text-gray-700 group-hover:text-red-600 transition-colors font-medium">{opt.label}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Event Type */}
      <FilterSection title="Event Type">
        <div className="flex flex-col gap-2">
          {eventTypeOptions.map((type) => (
            <label key={`etype-${type}`} className="flex items-center gap-2.5 cursor-pointer group">
              <input
                type="checkbox"
                checked={filters.eventTypes.includes(type)}
                onChange={() => toggleEventType(type)}
                className="w-4 h-4 accent-red-600 rounded"
              />
              <span className="text-sm text-gray-700 group-hover:text-red-600 transition-colors font-medium">{type}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Price Range */}
      <FilterSection title="Full Day Price (₹)">
        <div className="px-1">
          <div className="flex justify-between text-xs text-gray-500 font-medium mb-2">
            <span>₹0</span>
            <span className="text-red-600 font-bold">
              Up to ₹{(filters.priceMax / 1000).toFixed(0)}K
            </span>
          </div>
          <input
            type="range"
            min={0}
            max={300000}
            step={5000}
            value={filters.priceMax}
            onChange={(e) => setFilters((f) => ({ ...f, priceMax: Number(e.target.value) }))}
            className="w-full accent-red-600"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>₹0</span>
            <span>₹3L</span>
          </div>
        </div>
      </FilterSection>

      {/* Capacity */}
      <FilterSection title="Minimum Capacity">
        <div className="px-1">
          <div className="flex justify-between text-xs text-gray-500 font-medium mb-2">
            <span>Any</span>
            <span className="text-red-600 font-bold">{filters.capacityMin}+ guests</span>
          </div>
          <input
            type="range"
            min={0}
            max={1000}
            step={50}
            value={filters.capacityMin}
            onChange={(e) => setFilters((f) => ({ ...f, capacityMin: Number(e.target.value) }))}
            className="w-full accent-red-600"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>Any</span>
            <span>1000+</span>
          </div>
        </div>
      </FilterSection>

      {/* Rating */}
      <FilterSection title="Minimum Rating">
        <div className="flex flex-col gap-2">
          <label className="flex items-center gap-2.5 cursor-pointer group">
            <input
              type="radio"
              name="rating"
              value={0}
              checked={filters.rating === 0}
              onChange={() => setFilters((f) => ({ ...f, rating: 0 }))}
              className="w-4 h-4 accent-red-600"
            />
            <span className="text-sm text-gray-700 group-hover:text-red-600 transition-colors font-medium">Any rating</span>
          </label>
          {ratingOptions.map((opt) => (
            <label key={`rating-${opt.value}`} className="flex items-center gap-2.5 cursor-pointer group">
              <input
                type="radio"
                name="rating"
                value={opt.value}
                checked={filters.rating === opt.value}
                onChange={() => setFilters((f) => ({ ...f, rating: opt.value }))}
                className="w-4 h-4 accent-red-600"
              />
              <span className="text-sm text-gray-700 group-hover:text-red-600 transition-colors font-medium">{opt.label}</span>
            </label>
          ))}
        </div>
      </FilterSection>

      {/* Amenities */}
      <FilterSection title="Amenities" defaultOpen={false}>
        <div className="flex flex-wrap gap-2">
          {amenityOptions.map((amenity) => (
            <button
              key={`am-${amenity}`}
              onClick={() => toggleAmenity(amenity)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-all ${
                filters.amenities.includes(amenity)
                  ? 'bg-red-600 text-white border-red-600' :'bg-white text-gray-600 border-gray-200 hover:border-red-300 hover:text-red-600'
              }`}
            >
              {amenity}
            </button>
          ))}
        </div>
      </FilterSection>
    </div>
  );
}